/**
 * Pack 12.C — Tenant Theme Context (Task #1035)
 *
 * One-stop provider that:
 *   - reads the active tenant's `themePrefs` from `/api/tenant-config`
 *     (already fetched by the auth flow; no extra round-trip);
 *   - applies CSS variables on `document.body` for density (--tenant-density-*),
 *     font scale (--tenant-font-scale), accent color (--tenant-accent), and
 *     primary color (--tenant-primary);
 *   - applies the `dark` class on `<html>` when colorMode resolves to dark
 *     (auto = follow `prefers-color-scheme`).
 *
 * Density translates to a multiplier the design system reads via
 * --tenant-density-multiplier. Pages that adopt density-aware spacing use
 * `var(--tenant-density-multiplier, 1)` against their padding values.
 *
 * Font scale (default = 16px / large = 18px) sets `--tenant-font-scale`
 * which the html `font-size` is anchored to in design-system/tokens.css.
 *
 * The provider is mounted once at the app root (App.tsx). Pages only need
 * `useTheme()` if they want to read or override the prefs at runtime
 * (preview, etc); the visual application is automatic.
 *
 * Non-component exports (useTheme, ThemeContextValue) live in ./theme-hook
 * so Vite Fast Refresh can hot-swap this file independently.
 */

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  resolveTenantThemePrefs,
  type TenantThemeColorMode,
  type TenantThemeDensity,
  type TenantThemeFontScale,
  type TenantThemePrefs,
} from "@shared/schema";
import { ThemeContext } from "./theme-hook";
export { useTheme } from "./theme-hook";
export type { ThemeContextValue } from "./theme-hook";

type UserAppearance = "light" | "dark" | "auto";

const USER_APPEARANCE_KEY = "nashid.user.appearance.v1";

function readUserAppearance(): UserAppearance {
  try {
    const v = localStorage.getItem(USER_APPEARANCE_KEY);
    if (v === "light" || v === "dark" || v === "auto") return v;
  } catch {}
  return "auto";
}

const DENSITY_MULTIPLIER: Record<TenantThemeDensity, number> = {
  compact: 0.75,
  comfortable: 1,
  spacious: 1.25,
};

const FONT_SCALE_PX: Record<TenantThemeFontScale, number> = {
  default: 16,
  large: 18,
};

interface ResolvedTheme {
  primaryColor: string | null;
  accentColor: string | null;
  density: TenantThemeDensity;
  fontScale: TenantThemeFontScale;
  colorMode: TenantThemeColorMode;
}

function detectSystemDarkMode(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function useSystemDarkMode(): boolean {
  if (typeof window === "undefined") return false;
  return detectSystemDarkMode();
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { data: tenantConfig } = useQuery<{ themePrefs?: TenantThemePrefs | null }>({
    queryKey: ["/api/tenant-config"],
    enabled: true,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const prefs = useMemo<ResolvedTheme>(() => {
    return resolveTenantThemePrefs(tenantConfig?.themePrefs ?? null) as ResolvedTheme;
  }, [tenantConfig?.themePrefs]);

  const [userAppearance, setUserAppearance] = useState<UserAppearance>(readUserAppearance);
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === USER_APPEARANCE_KEY && (e.newValue === "light" || e.newValue === "dark" || e.newValue === "auto")) {
        setUserAppearance(e.newValue as UserAppearance);
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const resolvedColorMode: TenantThemeColorMode =
    userAppearance !== "auto" ? userAppearance : prefs.colorMode;

  const systemDark = useSystemDarkMode();
  const effectiveMode: "light" | "dark" =
    resolvedColorMode === "dark" ? "dark" : resolvedColorMode === "light" ? "light" : systemDark ? "dark" : "light";

  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const multiplier = DENSITY_MULTIPLIER[prefs.density];
    body.style.setProperty("--tenant-density-multiplier", String(multiplier));
    body.style.setProperty("--tenant-font-scale", `${FONT_SCALE_PX[prefs.fontScale]}px`);
    html.style.fontSize = `${FONT_SCALE_PX[prefs.fontScale]}px`;
    body.dataset.density = prefs.density;
    body.dataset.fontScale = prefs.fontScale;
    body.dataset.colorMode = resolvedColorMode;

    if (prefs.accentColor) {
      body.style.setProperty("--tenant-accent", prefs.accentColor);
    } else {
      body.style.removeProperty("--tenant-accent");
    }

    if (effectiveMode === "dark") {
      html.classList.add("dark");
    } else {
      html.classList.remove("dark");
    }

    return () => {
      // Don't tear down on unmount — the provider is mounted at the app root
      // and we want the styles to persist until the next provider state.
    };
  }, [prefs.density, prefs.fontScale, resolvedColorMode, prefs.accentColor, effectiveMode]);

  useEffect(() => {
    if (resolvedColorMode !== "auto" || typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      if (mq.matches) document.documentElement.classList.add("dark");
      else document.documentElement.classList.remove("dark");
    };
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [resolvedColorMode]);

  const value = useMemo(
    () => ({ prefs, effectiveMode, densityMultiplier: DENSITY_MULTIPLIER[prefs.density] }),
    [prefs, effectiveMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
